// AI assistants (MCP) — failing tools up front, calls per day with their
// errors, calls by tool, connected apps, connects vs disconnects.

import clsx from "clsx";
import { MCP_TOOL_KINDS, type AdminAssistants } from "@paceroo/domain/views/admin";
import { Card, ErrorState, LoadingState, Note, PageHeader, StatGrid } from "../components/ui";
import { useAdminQuery } from "../lib/api";
import { fDay, fRel, n0, parseWhen } from "../lib/format";
import { isHot, sum } from "../lib/series";
import { useRange } from "../lib/useRange";
import { useShell } from "../shell/context";
import s from "./Assistants.module.css";

const rate = (errors: number, calls: number) => `${calls ? ((errors / calls) * 100).toFixed(1) : "0.0"}%`;

export function Assistants() {
  const range = useRange();
  const { bp, focusSearch } = useShell();
  const q = useAdminQuery<AdminAssistants>(`assistants?days=${range.days}`);
  if (q.error && !q.data) return <ErrorState page="AI assistants" error={q.error} onRetry={q.reload} onFind={focusSearch} />;
  if (!q.data) return <LoadingState page="AI assistants" />;

  const d = q.data;
  const mobile = bp === "mobile";
  const days = d.range.days;
  const tools = d.tools ?? [];
  const hot = tools.filter((t) => isHot(t.calls, t.errors));
  const toolMax = Math.max(1, ...tools.map((t) => t.calls));
  const calls = d.daily?.calls ?? [];
  const errors = d.daily?.errors ?? [];
  const callMax = Math.max(1, ...calls);
  const weekly = d.weekly ?? [];
  const weekMax = Math.max(1, ...weekly.flatMap((w) => [w.connects, w.disconnects]));
  const axis = [...new Set([0, 1, 2, 3, 4].map((k) => fDay(d.days[Math.round((k * (d.days.length - 1)) / 4)]!)))];
  const now = new Date();

  return (
    <div className={s.page}>
      <PageHeader kicker="AI assistants · MCP" before="Assistants, " accent="at work." />

      {hot.map((t) => (
        <div key={t.tool} className={s.hot} role="alert">
          <span className={s.hotDot} />
          <span className={s.hotText}>
            <span className={s.hotTitle}>
              <span className="t-mono-sm">{t.tool}</span> is failing {rate(t.errors, t.calls)} of calls
            </span>
            <span className="t-mono-xs ink-soft">
              {n0(t.errors)} of {n0(t.calls)} calls in the last {days} days
            </span>
          </span>
        </div>
      ))}

      {d.analytics === "unavailable" && <Note>Calls, errors and tool usage need Analytics Engine to be queryable in this environment. Connected apps below are complete.</Note>}
      {days >= 90 && d.analytics === "available" && <Note>Raw events are kept for about 3 months, so the start of a 90-day range may be incomplete.</Note>}

      <StatGrid
        cols={mobile ? 2 : 5}
        valueSize={24}
        stats={[
          { label: "Connected apps", value: n0(d.connectedApps) },
          { label: "Active MCP runners", value: d.activeRunners === null ? "—" : n0(d.activeRunners) },
          { label: "Calls", value: d.calls === null ? "—" : n0(d.calls) },
          { label: "Error rate", value: d.calls === null || d.errors === null ? "—" : rate(d.errors, d.calls) },
          { label: "Rate-limited", value: d.rateLimited === null ? "—" : n0(d.rateLimited) },
        ]}
      />

      {d.daily && (
        <Card
          head={
            <>
              <span className="kicker ink-mute">Calls per day</span>
              <span className={s.legend}>
                <span className={clsx("t-mono-xs ink-soft", s.legendItem)}>
                  <span className={s.swatch} style={{ background: "var(--ink-soft)" }} />
                  ok
                </span>
                <span className={clsx("t-mono-xs ink-soft", s.legendItem)}>
                  <span className={s.swatch} style={{ background: "var(--alert)" }} />
                  error
                </span>
              </span>
            </>
          }
        >
          <div className={s.chart}>
            <div className={s.calls} style={{ height: mobile ? 90 : 140, gap: days >= 90 ? 1 : 2 }}>
              {calls.map((n, i) => (
                <div
                  key={i}
                  className={s.callCol}
                  title={`${fDay(d.days[i]!)} · ${n0(n)} calls · ${n0(errors[i] ?? 0)} errors`}
                  style={{ height: `${n ? Math.max(4, (n / callMax) * 100) : 0}%` }}
                >
                  <div className={s.callErr} style={{ height: `${n ? Math.min(100, ((errors[i] ?? 0) / n) * 100) : 0}%` }} />
                </div>
              ))}
              {sum(calls) === 0 && (
                <div className={s.callsEmpty}>
                  <span className="t-body-sm ink-mute">No assistant calls yet.</span>
                </div>
              )}
            </div>
            <div className={s.axis}>
              {axis.map((a) => (
                <span key={a} className="t-mono-xs ink-mute">
                  {a}
                </span>
              ))}
            </div>
          </div>
        </Card>
      )}

      <div className={s.pair}>
        {d.tools && (
          <Card head={<span className="kicker ink-mute">Calls by tool</span>}>
            <div className={s.tools}>
              <div className={s.toolRow}>
                <span className="t-mono-xs ink-mute">TOOL</span>
                <span />
                <span />
                <span className={clsx("t-mono-xs ink-mute", s.right)}>CALLS</span>
                <span className={clsx("t-mono-xs ink-mute", s.right)}>ERR</span>
              </div>
              {tools.map((t) => (
                <div key={t.tool} className={s.toolRow} title={t.calls ? `avg ${n0(t.avgMs)} ms` : undefined}>
                  <span className={clsx("t-mono-sm", s.ellipsis)}>{t.tool}</span>
                  <span className="t-mono-xs ink-mute">{MCP_TOOL_KINDS[t.tool] ?? "—"}</span>
                  <div className={s.track}>
                    <div className={s.fill} style={{ width: `${(t.calls / toolMax) * 100}%` }}>
                      <div className={s.err} style={{ width: `${t.calls ? (t.errors / t.calls) * 100 : 0}%`, minWidth: t.errors ? 2 : 0 }} />
                    </div>
                  </div>
                  <span className={clsx("t-mono-sm", s.right)}>{n0(t.calls)}</span>
                  <span className={clsx("t-mono-sm", s.right)} style={{ color: isHot(t.calls, t.errors) ? "var(--alert)" : "var(--ink-soft)" }}>
                    {rate(t.errors, t.calls)}
                  </span>
                </div>
              ))}
            </div>
          </Card>
        )}

        <div className={s.side}>
          <Card head={<span className="kicker ink-mute">Connected apps</span>} bodyClass={s.flush}>
            {d.clients.map((c) => (
              <div key={c.clientId} className={s.client}>
                <span className={s.clientName}>
                  <span className="t-label">
                    {c.name ?? "Unnamed app"}
                    {c.local && <span className={clsx("t-mono-xs", s.local)}>LOCAL</span>}
                  </span>
                  <span className={clsx("t-mono-xs ink-mute", s.ellipsis)}>
                    {c.host ?? c.clientId} · {n0(c.readWrite)} read-write
                  </span>
                </span>
                <span className={clsx("t-mono-sm", s.right)}>{n0(c.runners)}</span>
                <span className={clsx("t-mono-sm", s.right)}>{c.calls === null ? "—" : n0(c.calls)}</span>
                <span className={clsx("t-mono-xs ink-mute", s.right)}>{c.lastCallAt ? fRel(parseWhen(c.lastCallAt), now) : "—"}</span>
              </div>
            ))}
            {d.clients.length > 0 ? (
              <div className={s.clientFoot}>
                <span />
                <span className={clsx("t-mono-xs ink-mute", s.right)}>RUNNERS</span>
                <span className={clsx("t-mono-xs ink-mute", s.right)}>CALLS</span>
                <span className={clsx("t-mono-xs ink-mute", s.right)}>LAST</span>
              </div>
            ) : (
              <p className={clsx("t-body-sm ink-mute", s.none)}>No assistant has connected yet.</p>
            )}
          </Card>

          {d.weekly && (
            <Card
              head={
                <>
                  <span className="kicker ink-mute">Connects vs disconnects · weekly</span>
                  <span className={s.legend}>
                    <span className={clsx("t-mono-xs ink-soft", s.legendItem)}>
                      <span className={s.swatch} style={{ background: "var(--ink)" }} />
                      connect
                    </span>
                    <span className={clsx("t-mono-xs ink-soft", s.legendItem)}>
                      <span className={s.swatchOutline} />
                      disconnect
                    </span>
                  </span>
                </>
              }
            >
              <div className={s.weeks}>
                <div className={s.weekBars}>
                  {weekly.map((w) => (
                    <div key={w.start} className={s.week} title={`${n0(w.connects)} connected · ${n0(w.disconnects)} disconnected`}>
                      <div className={s.connect} style={{ height: `${(w.connects / weekMax) * 100}%` }} />
                      <div className={s.disconnect} style={{ height: `${(w.disconnects / weekMax) * 100}%` }} />
                    </div>
                  ))}
                </div>
                <div className={s.weekLabels}>
                  {weekly.map((w) => (
                    <span key={w.start} className={clsx("t-mono-xs ink-mute", s.weekLabel)}>
                      {fDay(w.start)}
                    </span>
                  ))}
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
